/**
 * Live processing explanations ("ход обработки") streamed by the model before
 * the answer starts.
 *
 * The API only emits `thinking` events for models whose provider is confirmed
 * to expose user-visible explanations, so this module never fabricates text: it
 * just accumulates what arrives, keyed by the assistant message it belongs to.
 *
 * Explanations are session-only: they are never merged into `Message.content`,
 * never copied/shared with the answer, and never persisted.
 */

/** Hard cap so a very long reasoning stream cannot grow without bound. */
export const MAX_REASONING_CHARS = 4000;

export type ReasoningByMessageId = Readonly<Record<string, string>>;

export const EMPTY_REASONING: ReasoningByMessageId = Object.freeze({});

function clamp(text: string): string {
  if (text.length <= MAX_REASONING_CHARS) return text;
  return `…${text.slice(text.length - MAX_REASONING_CHARS)}`;
}

/**
 * Append one explanation chunk to the given message.
 *
 * Malformed chunks (missing id, non-string text) and chunks that would only
 * create an empty entry are ignored, so a model that advertises support but
 * sends nothing useful never produces a blank panel.
 */
export function appendReasoning(
  state: ReasoningByMessageId,
  messageId: string | null | undefined,
  text: unknown,
): ReasoningByMessageId {
  if (!messageId) return state;
  if (typeof text !== 'string' || text.length === 0) return state;
  const current = state[messageId] ?? '';
  if (current === '' && text.trim() === '') return state;
  return { ...state, [messageId]: clamp(current + text) };
}

/** Drop explanations whose message no longer exists (new turn, edit, switch). */
export function pruneReasoning(
  state: ReasoningByMessageId,
  keepIds: Iterable<string>,
): ReasoningByMessageId {
  const keys = Object.keys(state);
  if (keys.length === 0) return state;
  const keep = new Set(keepIds);
  const removed = keys.filter((id) => !keep.has(id));
  if (removed.length === 0) return state;
  const next: Record<string, string> = {};
  for (const id of keys) {
    if (keep.has(id)) next[id] = state[id]!;
  }
  return next;
}

/** Text to render for a message, or `null` when there is nothing to show. */
export function reasoningTextFor(
  state: ReasoningByMessageId,
  messageId: string,
): string | null {
  const text = state[messageId];
  return text && text.trim().length > 0 ? text : null;
}
